import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, Flame, ChevronRight, Lock } from "lucide-react";

const BASE_CATS = ["All", "Strength", "Conditioning", "Mobility", "Core", "Beginner", "Advanced"];

export default function Workouts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isCoachingClient = user?.subscription_tier === "coaching";
  const CATS = isCoachingClient ? ["All", "Exclusive", ...BASE_CATS.slice(1)] : BASE_CATS;
  const [cat, setCat] = useState(location.state?.cat && (location.state.cat !== "Exclusive" || isCoachingClient) ? location.state.cat : "All");
  const [pat, setPat] = useState("All");
  const [patterns, setPatterns] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/workouts/patterns").then(({ data }) => setPatterns(["All", ...data])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/workouts", { params: { category: cat, movement_pattern: pat } })
      .then(({ data }) => setWorkouts(data))
      .finally(() => setLoading(false));
  }, [cat, pat]);

  return (
    <AppShell title="Workouts">
      <div className="-mx-4 px-4 sticky top-[100px] z-30 bg-background/80 backdrop-blur pb-2">
        <div className="flex gap-2 overflow-x-auto rd-scrollbar pb-1">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c.toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm border transition-colors ${
                cat === c ? "bg-[#ccff0c] text-black border-[#ccff0c]" : "bg-card text-muted-foreground border-border"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {patterns.length > 1 && (
          <div className="flex gap-2 overflow-x-auto rd-scrollbar pb-1 mt-2">
            {patterns.map((p) => (
              <button
                key={p}
                data-testid={`pat-${p.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => setPat(p)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs border transition-colors ${
                  pat === p ? "bg-foreground text-background border-foreground" : "bg-card text-muted-foreground border-border"
                }`}
              >
                {p === "All" ? "All patterns" : p}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3 mt-3">
        {loading ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-2xl bg-card" />)
        ) : workouts.length === 0 ? (
          <p className="text-muted-foreground text-center py-10">No workouts in this category yet.</p>
        ) : (
          workouts.map((w) => (
            <Card
              key={w.id}
              data-testid={`workout-card-${w.id}`}
              onClick={() => navigate(`/workouts/${w.id}`)}
              className="rounded-2xl bg-card border-border p-4 cursor-pointer hover:border-[#ccff0c]/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-secondary border border-border text-muted-foreground text-[11px]">{w.category}</Badge>
                    <Badge className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/20 text-[11px]">{w.difficulty}</Badge>
                    {w.assigned_client_id && (
                      <Badge data-testid={`exclusive-tag-${w.id}`} className="bg-[#ccff0c] text-black text-[11px] flex items-center gap-0.5"><Lock className="h-3 w-3" /> Exclusive</Badge>
                    )}
                  </div>
                  <h3 className="font-display text-lg font-bold">{w.title}</h3>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {w.duration_min} min</span>
                    <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5" /> {(w.equipment || []).slice(0,2).join(", ") || "No equipment"}</span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </Card>
          ))
        )}
      </div>
    </AppShell>
  );
}
